import { type FC, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  ActionButtonSize,
  HStack,
  Icons,
  TriggerButton,
  TriggerButtonIntent,
  TriggerButtonSize,
  VStack,
} from "widgets";

import { pluralizeWord } from "@/app-utils/string.ts";
import { type Notification, type Organism, type PlaybookEvent } from "@/graphql.ts";
import { isBedFile } from "@/modules/files/utils/file.js";
import { addBedFile } from "@/redux/modules/genome-browser.ts";
import { getParentRoute, getPlaybookEventRoute, getWorkflowEventRoute } from "@/redux/modules/saved-view.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { NOTIFICATION_STATUS, PLAYBOOK_TYPE, WORKFLOW_NOTIFICATION_SUBTYPE } from "@/settings/settings.ts";

import { SelMessage } from "../../messages/SelMessage.js";
import { SmartLink } from "../core/SmartLink.js";

/**
 * A stateless component representing a workflow notification
 */

export interface WorkflowNotificationProps {
  notification: Notification;
}

const WorkflowNotificationCmpt: FC<WorkflowNotificationProps> = ({ notification }) => {
  const workflowNotification = notification.workflowNotification;
  const playbookEvent = notification.playbookEvent;

  let contents: ReactNode = null;
  if (workflowNotification?.subtype === WORKFLOW_NOTIFICATION_SUBTYPE.SAMPLE_STATUS_CHANGE) {
    contents = (
      <>
        <SampleStatusUpdateNotificationContent notification={notification} />
        {notification.playbookEvent && <PlaybookStatusUpdateNotificationContent notification={notification} />}
      </>
    );
  } else if (workflowNotification?.subtype === WORKFLOW_NOTIFICATION_SUBTYPE.PLAYBOOK_EVENT_CHANGE) {
    contents = <PlaybookStatusUpdateNotificationContent notification={notification} />;
  } else if (workflowNotification?.subtype === WORKFLOW_NOTIFICATION_SUBTYPE.WORKFLOW_EVENT_CHANGE) {
    contents = <WorkflowStatusUpdateNotificationContent notification={notification} />;
  } else if (playbookEvent) {
    // case of P1 related Playbooks => no workflowNotification
    contents = <PlaybookStatusUpdateNotificationContent notification={notification} />;
  } else {
    console.error("Unsupported workflow notification of subtype ", workflowNotification?.subtype, notification);
  }

  return <span key={"workflow-notification-" + notification.id + "-"}>{contents}</span>;
};

const WorkflowStatusUpdateNotificationContent: FC<WorkflowNotificationProps> = ({ notification }) => {
  const workflowEvent = notification.workflowEvent;
  const navigate = useNavigate();

  return (
    <>
      <div>
        <SelMessage
          id={"playbook.execution.workflowEventStatusUpdate"}
          values={{
            workflowEventName: `${workflowEvent.alias}`,
            status: notification.status,
          }}
        />
      </div>
      <div>
        <ActionButton
          label={"Open in Processor"}
          onPress={() => navigate(getWorkflowEventRoute(notification.workflowEvent!))}
          intent={
            notification.status === NOTIFICATION_STATUS.ERROR || notification.status === NOTIFICATION_STATUS.TERMINATED
              ? ActionButtonIntent.Danger
              : ActionButtonIntent.Outlined
          }
          size={ActionButtonSize.Small}
          icon={"arrow_forward"}
          iconPlacement={ActionButtonIconPlacement.Right}
          style={{ margin: "5px 0" }}
        />
      </div>
    </>
  );
};

const PlaybookStatusUpdateNotificationContent: FC<WorkflowNotificationProps> = ({ notification }) => {
  const playbookEvent = notification.playbookEvent;
  const navigate = useNavigate();

  return (
    <>
      <div>
        <SelMessage
          id={"playbook.execution.eventStatusUpdate"}
          values={{
            playbookEventName: `${playbookEvent.playbook?.name} ${
              playbookEvent.alias ? `(${playbookEvent.alias})` : ""
            }`,
            status: notification.status,
          }}
        />
      </div>
      <div>
        <ActionButton
          label={"Event Summary"}
          onPress={() => navigate(getPlaybookEventRoute(notification.playbookEvent!))}
          intent={
            notification.status === NOTIFICATION_STATUS.ERROR || notification.status === NOTIFICATION_STATUS.TERMINATED
              ? ActionButtonIntent.Danger
              : ActionButtonIntent.Outlined
          }
          size={ActionButtonSize.Small}
          icon={"arrow_forward"}
          iconPlacement={ActionButtonIconPlacement.Right}
          style={{ margin: "5px 0" }}
        />
      </div>
      {playbookEvent.playbook?.type?.id === PLAYBOOK_TYPE.RANGE_ANALYSIS && (
        <RangeAnalysisNotificationResults playbookEvent={notification.playbookEvent!} />
      )}
    </>
  );
};

const SampleStatusUpdateNotificationContent: FC<WorkflowNotificationProps> = ({ notification }) => {
  const workflowNotification = notification.workflowNotification;
  const navigate = useNavigate();

  return (
    <>
      <div>
        <SelMessage
          id={"playbook.execution.parentStatusUpdate"}
          values={{ sampleParentName: workflowNotification.parent?.name, status: workflowNotification.status?.name }}
        />
      </div>
      <div>
        <ActionButton
          label={"Sample Parent"}
          onPress={() => navigate(getParentRoute(workflowNotification.parent!))}
          intent={ActionButtonIntent.Outlined}
          size={ActionButtonSize.Small}
          icon={"arrow_forward"}
          iconPlacement={ActionButtonIconPlacement.Right}
          style={{ margin: "5px 0" }}
        />
      </div>
    </>
  );
};

interface IRangeAnalysisNotificationResults {
  playbookEvent: PlaybookEvent;
}

const RangeAnalysisNotificationResults: FC<IRangeAnalysisNotificationResults> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const location = useLocation();
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome);

  const genomeEventInput = props.playbookEvent.inputs?.find((inputResource) =>
    inputResource.resources?.find((resource) => resource.organism),
  );
  const genomeResource = genomeEventInput?.resources?.find((resource) => resource.organism);
  const rangeAnalysisGenomeId = genomeResource?.organism.id;
  const genomeViewMatchesEvent: boolean =
    (matchPath(`/:entity/:id/genome/*`, location.pathname) || matchPath(`/visualizer/genome/*`, location.pathname)) &&
    genome?.id === rangeAnalysisGenomeId;

  const fileLinks =
    rangeAnalysisGenomeId &&
    props.playbookEvent.results
      .filter((r) => r?.file && isBedFile(r.file))
      .map((r, i) => {
        return (
          <HStack key={`file-link-${i}`}>
            <SmartLink
              forceExternal
              to={`/organism/${rangeAnalysisGenomeId}/genome?add_tracks=bed&bed_files=${r.file.id}`}
            >
              {r.file.fileName}
            </SmartLink>
            {genomeViewMatchesEvent && (
              <TriggerButton
                label={"Add to Current View"}
                onPress={() => void dispatch(addBedFile(r.file.id))}
                size={TriggerButtonSize.Small}
                icon={<img src={Icons.Action.GenomeViewer} width={16} height={16} alt={"Genome Viewer"} />}
                intent={TriggerButtonIntent.Outlined}
              />
            )}
          </HStack>
        );
      });

  return (
    fileLinks?.length > 0 && (
      <VStack spacing={3}>
        {`Open result ${pluralizeWord("file", fileLinks.length)} in Genome Viewer: `}
        {fileLinks}
      </VStack>
    )
  );
};

export default WorkflowNotificationCmpt;
